import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	updateImages,
	updateUser,
	getAllUsers,
} from '../../../../store/user.js';
import SublyApi from '../../../../helpers/Api.js';

export const useClientImages = (client) => {
	const dispatch = useDispatch();
	const token = useSelector((st) => st.admin.token); // Get token from Redux
	const [uploading, setUploading] = useState({});
	const [error, setError] = useState(null);
	const [images, setImages] = useState({});
	const [loading, setLoading] = useState(false);

	// Load images from API when client changes
	useEffect(() => {
		if (client?.username) {
			loadImages();
		}
	}, [client?.username]);

	// Load all images for the client
	const loadImages = useCallback(async () => {
		if (!client?.username || !token) return;

		setLoading(true);
		try {
			const response = await SublyApi.getUserImages(
				client.username,
				{ organized: true },
				token
			);

			const imageData = response.images || {};
			setImages({ ...imageData });
		} catch (err) {
			console.error('Error loading images:', err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [client?.username, token]);

	// Get images by type (with multi-slot support)
	const getImagesByType = useCallback(
		(imageType) => {
			// Map frontend image type to backend key
			const backendKey = imageType === 'broker_logo' ? 'brokerLogo' : imageType;

			console.log(`🔍 getImagesByType DEBUG:`, {
				requestedType: imageType,
				mappedBackendKey: backendKey,
				availableKeys: Object.keys(images),
				allImagesStructure: images,
				foundData: images[backendKey],
			});

			// Ensure we always return an array
			const imageData = images[backendKey];
			console.log(`getImagesByType(${imageType}):`, {
				frontendType: imageType,
				backendKey,
				imageData,
				allImages: images,
			});

			// If imageData exists and is an array, return it
			if (Array.isArray(imageData)) {
				return imageData;
			}

			// If imageData exists but is not an array, wrap it in an array
			if (imageData) {
				return [imageData];
			}

			// If no imageData, fallback to legacy single image for backward compatibility
			const legacyImageUrl = client?.[imageType];
			if (legacyImageUrl) {
				return [
					{
						id: `legacy-${client.username}-${imageType}`,
						imageType: imageType,
						imageSlot: 1,
						imageUrl: legacyImageUrl,
						caption: null,
						isPrimary: true,
						uploadedAt: new Date().toISOString(),
						isLegacy: true,
					},
				];
			}

			// Return empty array as fallback
			return [];
		},
		[images, client]
	);

	// Get image by slot
	const getImageBySlot = useCallback(
		(imageType, slotNumber) => {
			const imagesByType = getImagesByType(imageType);
			return imagesByType.find((img) => img.imageSlot === slotNumber) || null;
		},
		[getImagesByType]
	);

	// Upload image with multi-slot support
	const uploadImage = useCallback(
		async (imageType, slotNumber, file, options = {}) => {
			if (!client || !file) return false;

			console.log('📁 FILE DEBUG - Upload starting:', {
				fileName: file.name,
				fileSize: file.size,
				fileType: file.type,
				lastModified: file.lastModified,
				fileObject: file,
			});

			const slotKey = `${imageType}-${slotNumber}`;
			setUploading((prev) => ({ ...prev, [slotKey]: true }));
			setError(null);

			try {
				// Check if slot is occupied
				const existingImage = getImageBySlot(imageType, slotNumber);

				if (existingImage && !existingImage.isLegacy) {
					// Replace existing image
					await SublyApi.replaceUserImage(existingImage.id, file, token);
				} else {
					// Upload new image
					await SublyApi.uploadUserImage(
						client.username,
						{
							file,
							imageType,
							imageSlot: slotNumber,
							caption: options.caption,
							isPrimary: options.isPrimary || slotNumber === 1,
						},
						token
					);

					// If replacing legacy image, clear it from user profile
					if (existingImage?.isLegacy) {
						await dispatch(
							updateUser({
								username: client.username,
								data: { [imageType]: '' },
								token,
							})
						).unwrap();
					}
				}

				// Reload images to get updated data
				await loadImages();

				// Refresh user data for legacy compatibility
				setTimeout(() => {
					dispatch(getAllUsers({ token }));
				}, 1500);

				return true;
			} catch (err) {
				console.error(`Error uploading ${imageType}:`, err);
				setError(err.message);
				return false;
			} finally {
				setUploading((prev) => ({ ...prev, [slotKey]: false }));
			}
		},
		[client, token, getImageBySlot, loadImages, dispatch]
	);

	// Remove image (backward compatible)
	const removeImage = useCallback(
		async (imageType, slotNumber = 1) => {
			if (!client) return false;

			const slotKey = `${imageType}-${slotNumber}`;
			setUploading((prev) => ({ ...prev, [slotKey]: true }));
			setError(null);

			try {
				const existingImage = getImageBySlot(imageType, slotNumber);

				if (existingImage && !existingImage.isLegacy) {
					// Delete from new API
					await SublyApi.deleteUserImage(existingImage.id, token);
				}

				// Also clear from legacy user profile for backward compatibility
				await dispatch(
					updateUser({
						username: client.username,
						data: { [imageType]: '' },
						token,
					})
				).unwrap();

				// Reload images
				await loadImages();

				// Refresh user data
				setTimeout(() => {
					dispatch(getAllUsers({ token }));
				}, 1500);

				return true;
			} catch (err) {
				console.error(`Error removing ${imageType}:`, err);
				setError(err.message);
				return false;
			} finally {
				setUploading((prev) => ({ ...prev, [slotKey]: false }));
			}
		},
		[client, token, getImageBySlot, loadImages, dispatch]
	);

	// Delete image by ID
	const deleteImage = useCallback(
		async (imageId) => {
			console.log('🔥 HOOK deleteImage called with ID:', imageId);
			console.log('🔥 HOOK deleteImage - imageId type:', typeof imageId);
			console.log(
				'🔥 HOOK deleteImage - imageId value:',
				JSON.stringify(imageId)
			);
			console.log('🔥 HOOK deleteImage - token exists:', !!token);

			if (!imageId) {
				console.warn('🔥 HOOK deleteImage - No imageId provided');
				return false;
			}

			// Ensure imageId is a string or number before calling startsWith
			const idString = String(imageId);
			console.log('🔥 HOOK deleteImage - converted to string:', idString);

			// Handle legacy ID format: "legacy-username-imageType"
			if (idString.startsWith('legacy-')) {
				console.log(
					'🔥 HOOK deleteImage - Legacy image detected, calling removeImage'
				);
				const parts = idString.split('-');
				const imageType = parts[parts.length - 1];
				return await removeImage(imageType, 1);
			}

			console.log(
				'🔥 HOOK deleteImage - Setting uploading state for ID:',
				imageId
			);
			setUploading((prev) => ({ ...prev, [imageId]: true }));
			setError(null);

			try {
				console.log(
					'🔥 HOOK deleteImage - About to call SublyApi.deleteUserImage'
				);
				const apiResult = await SublyApi.deleteUserImage(idString, token);
				console.log('🔥 HOOK deleteImage - API call result:', apiResult);

				console.log('🔥 HOOK deleteImage - About to reload images');
				await loadImages();
				console.log('🔥 HOOK deleteImage - Images reloaded successfully');

				return true;
			} catch (err) {
				console.error('🔥 HOOK deleteImage - Error occurred:', err);
				console.error('🔥 HOOK deleteImage - Error details:', {
					message: err.message,
					status: err.status,
					response: err.response,
				});
				setError(err.message);
				return false;
			} finally {
				console.log('🔥 HOOK deleteImage - Clearing uploading state');
				setUploading((prev) => ({ ...prev, [imageId]: false }));
			}
		},
		[token, loadImages, removeImage]
	);

	// Update caption
	const updateCaption = useCallback(
		async (imageId, caption) => {
			if (!imageId) return false;

			// Skip legacy images
			if (imageId.startsWith('legacy-')) {
				console.warn('Caption updates not supported for legacy images');
				return true;
			}

			try {
				await SublyApi.updateUserImage(imageId, { caption }, token);
				await loadImages();
				return true;
			} catch (err) {
				console.error('Error updating caption:', err);
				setError(err.message);
				return false;
			}
		},
		[token, loadImages]
	);

	// Set as primary image
	const setPrimary = useCallback(
		async (imageId) => {
			if (!imageId || String(imageId).startsWith('legacy-')) {
				return false;
			}

			try {
				await SublyApi.updateUserImage(imageId, { isPrimary: true }, token);
				await loadImages();
				return true;
			} catch (err) {
				console.error('Error setting primary image:', err);
				setError(err.message);
				return false;
			}
		},
		[token, loadImages]
	);

	// Get image URL (backward compatible)
	const getImageUrl = useCallback(
		(imageType, slotNumber = 1) => {
			const image = getImageBySlot(imageType, slotNumber);
			return image?.imageUrl || client?.[imageType] || null;
		},
		[getImageBySlot, client]
	);

	// Check if image exists (backward compatible)
	const hasImage = useCallback(
		(imageType, slotNumber = 1) => {
			return Boolean(getImageUrl(imageType, slotNumber));
		},
		[getImageUrl]
	);

	// Get primary image for a type
	const getPrimaryImage = useCallback(
		(imageType) => {
			const imagesByType = getImagesByType(imageType);
			return (
				imagesByType.find((img) => img.isPrimary) || imagesByType[0] || null
			);
		},
		[getImagesByType]
	);

	// Get next available slot
	const getNextAvailableSlot = useCallback(
		async (imageType) => {
			try {
				const response = await SublyApi.getNextAvailableSlot(
					client?.username,
					imageType,
					token
				);
				return response.nextSlot;
			} catch (err) {
				console.error('Error getting next slot:', err);
				return 1; // Default to slot 1
			}
		},
		[client?.username, token]
	);

	return {
		// Multi-slot support
		images,
		loading,
		error,
		loadImages,
		uploadImage,
		removeImage,
		deleteImage,
		updateCaption,
		setPrimary,
		getImagesByType,
		getImageBySlot,
		getPrimaryImage,
		getNextAvailableSlot,

		// Backward compatibility
		getImageUrl,
		hasImage,
		uploading,
	};
};
